import { Signal } from 'rwwa-rx-state-machine'
import type {
  Selection,
  ToteSelection,
  FobPropositionSelection,
  MysteryDetails,
  EventDetails,
  RaceDetails,
  BetSlipResponse,
  FobBetReceiptResponse,
  BetSlipItem,
} from '@mobi/betslip/types'
import {
  AnalyticsBetData,
  keys as analyticsKeys,
  defaultData as analyticsDefaultData,
  BetConstruction,
} from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import * as Analytics from '@classic/Foundation/Analytics/Analytics'
import { trackBetConfirmation } from '@classic/Foundation/Analytics/Analytics.Bet'
import { BetErrorType, BetErrorTypeLiteral, ensureBetErrorTypeLiteral } from '@core/Data/betting'
import { trackBetSlipAdd } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { getFeatures } from '@core/State/FeatureFlags/Store/selectors'
import { store } from '@core/Store'

const defaultBetData = analyticsDefaultData.bet

type BetslipAnalyticsSubscriberParams = {
  confirmAllBetsSuccessful: Signal
  getSelection: GetSelection
  construction: BetConstruction
}

export function betslipAnalyticsSubscriber(props: BetslipAnalyticsSubscriberParams): void {
  const confirmSignalData: BetSlipResponse[] = props.confirmAllBetsSuccessful.data

  confirmSignalData.forEach(bet => {
    const isMultiBet = bet.legs && bet.legs.length > 1

    const step2BetData: AnalyticsBetData = {
      ...defaultBetData,
      construction: props.construction ?? 'betslip',
      productType: isMultiBet ? 'multi' : 'single',
      errorMessage: !bet.success && bet.error ? mapBetErrorTypeIfNonNullable(bet.error.type) : null,
    }

    Analytics.track(analyticsKeys.betAccountReview, { bet: step2BetData })

    // ----------------------------- Bet Step 3 -----------------------------
    if (bet.success) {
      if (isMultiBet) {
        const { ticketNumber, winInvestment, bonusBet } = bet.receipt as FobBetReceiptResponse

        const step3MultiBetData: AnalyticsBetData = {
          ...defaultBetData,
          specialOffers: null,
          id: ticketNumber,
          construction: props.construction ?? 'betslip',
          productType: 'multi',
          placementMethod: 'account',
          mixed: {
            fixed: {
              cost: winInvestment,
              numberOfTickets: 1,
              bonusCashUsed: bonusBet > 0,
              bonusCashAmount: bonusBet,
            },
          },
        }

        Analytics.track(analyticsKeys.betAccountProcessed, { bet: step3MultiBetData })
      } else {
        const betItem = props.getSelection(bet.id)
        if (!betItem) return

        const [selection, selectionDetails] = betItem
        const {
          ticketNumber,
          winInvestment = 0,
          placeInvestment = 0,
          specialOffers = [],
          betCost = 0,
          bonusBet,
        } = extractReceipt(bet.receipt)
        const features = getFeatures(store.getState())

        trackBetConfirmation({
          ticketNumber,
          selection,
          selectionDetails,
          specialOffers,
          betConstruction: props.construction,
          betCost,
          winInvestment,
          placeInvestment,
          features,
          betSource: null,
          bonusCashAmount: bonusBet ?? 0,
        })
      }
    }
  })
}

function mapBetErrorTypeIfNonNullable(
  betErrorType: Maybe<string | number | BetErrorType>
): Nullable<BetErrorTypeLiteral> {
  if (betErrorType === null || betErrorType === undefined || betErrorType === '') {
    return null
  }
  return ensureBetErrorTypeLiteral(betErrorType)
}

function extractReceipt(receipt: BetSlipResponse['receipt']): {
  ticketNumber: number
  winInvestment: number
  placeInvestment: number
  specialOffers: BetSlipResponse['specialOffers']
  betCost: number
  bonusBet?: number
} {
  if (!receipt) {
    return {
      ticketNumber: -1,
      winInvestment: 0,
      placeInvestment: 0,
      specialOffers: [],
      betCost: 0,
    }
  }

  if ('betCost' in receipt) {
    return {
      ticketNumber: receipt.ticketNumber,
      winInvestment: 0,
      placeInvestment: 0,
      specialOffers: [],
      betCost: receipt.betCost,
      bonusBet: receipt.bonusBet,
    }
  }

  return {
    ticketNumber: receipt.ticketNumber,
    winInvestment: receipt.winInvestment,
    placeInvestment: receipt.placeInvestment,
    specialOffers: receipt.specialOffers ?? [],
    betCost: 0,
    bonusBet: receipt.bonusBet,
  }
}

export const trackAddToBetslip = (addedItem: BetSlipItem) => {
  const { selection, selectionDetails, betSource } = addedItem

  if (betSource && selection && selectionDetails) {
    const betType =
      (selection as ToteSelection).betType || (selection as FobPropositionSelection).type

    const race =
      (selectionDetails as unknown as RaceDetails).races[0] ||
      (selectionDetails as MysteryDetails).race

    const { meetingCode: raceCode, meetingName } = race || {
      meetingName: null,
      meetingCode: null,
    }

    trackBetSlipAdd({ construction: 'quickbet', source: betSource, raceCode, meetingName, betType })
  }
}

// =====
// Types
// =====

type GetSelection = (id: string) => [Selection, EventDetails] | null
